import { Component, OnInit, Input, Output, EventEmitter } from "@angular/core";
import {
  UiUser,
  UserRole,
  UsersSearchParams,
} from "src/app/shared/models/ui-user.model";
import { DataElement, Doctor } from "src/app/shared/models/common";
import { keyCodes } from "src/app/shared/models/key-codes.model";
import { SharedService } from "src/app/shared/services/shared.service";
import { ValidationPipe } from "src/app/shared/components/ui/validation.pipe";
import { Subscription } from "rxjs";
import { UsersService } from "src/app/shared/services/users.service";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { Clinic } from "src/app/shared/models/clinic.model";
import { AuthService } from "src/app/shared/services/auth.service";
import {
  clinicList,
  doctorList,
  plantList,
  roleList,
} from "src/app/shared/ngrx/ngrx-selectors";
import { Store } from "@ngrx/store";
import { UPDATE_USER } from "src/app/shared/ngrx/ngrx-actions";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { SelectModule } from "primeng/select";
import { InputWrapComponent } from "src/app/shared/components/ui/input-wrap.component";
import { SpinnerComponent } from "src/app/shared/components/ui/spinner/spinner.component";
import { ClearMultiComponent } from "src/app/shared/components/ui/clear-multi.component";

@Component({
  selector: "user-edit",
  templateUrl: "./user-edit.component.html",
  styleUrls: ["./user-edit.component.scss"],
  imports: [
    CommonModule,
    FormsModule,
    InputWrapComponent,
    TranslateModule,
    SelectModule,
    SpinnerComponent,
    ClearMultiComponent
  ],
  standalone: true,
})
export class UserEditComponent implements OnInit {
  @Input() user: UiUser;
  @Output() onCancel = new EventEmitter();

  usernameAutoCompleteLoading: boolean = false;
  keyCodes = keyCodes;
  usernameValidationStr: string;
  invalidUsernameStr = "translate.users.usernameValidation";
  existingUserSw: boolean;
  checkUsernameSubscription: Subscription;
  saving: boolean;
  filteredDoctorList: Doctor[] = [];
  roleList: UserRole[] = [];

  get clinicList(): Clinic[] {
    return this.store.selectSignal(clinicList)();
  }

  get plantList(): DataElement[] {
    return this.store.selectSignal(plantList)();
  }

  get displayClinic(): boolean {
    return (
      this.user.role &&
      this.user.role.org == "CLINIC" &&
      this.user.role.id !== "DOCTOR"
    );
  }

  get displayPlant(): boolean {
    return this.user.role && this.user.role.org == "PLANT";
  }

  get displayDoctor(): boolean {
    return this.user.role && (this.user.role.id == "DOCTOR" || this.user.role.id == "CLINIC_OWNER");
  }

  /** Clinic is required for CLINIC roles except DOCTOR (doctors can be clinic-agnostic). */
  get clinicRequired(): boolean {
    return this.displayClinic && this.user.role?.id !== "DOCTOR";
  }

  get doctorRequired(): boolean {
    return this.displayDoctor && this.user.role?.id === "DOCTOR";
  }

  constructor(
    private readonly store: Store,
    private sharedService: SharedService,
    private validationPipe: ValidationPipe,
    private usersService: UsersService,
    private translateService: TranslateService,
    private authService: AuthService
  ) { }

  ngOnInit(): void {
    if (
      !this.user.isNewUser ||
      this.authService.currentUser.roleCode == "ADMIN"
    ) {
      this.roleList = this.store.selectSignal(roleList)();
    } else {
      this.roleList = this.store
        .selectSignal(roleList)()
        .filter(
          (r) =>
            r.id == "DOCTOR" || r.id == "CLINIC_TEAM" || r.id == "CLINIC_OWNER"
        );
    }

    if (this.user.isNewUser && !this.user.password) {
      let min = 10000000;
      let max = 99999999;
      this.user.password = (
        Math.floor(Math.random() * (max - min + 1)) + min
      ).toString();
    }

    if (!this.user.isNewUser) {
      if (this.user.clinic?.id != null) {
        this.user.clinic = this.clinicList.find(
          (c) => c.clinicId == this.user.clinic.id
        );
      }
      this.onClinicChanged();
    } else {
      this.onClinicChanged();
    }
  }

  get readonly_user(): boolean {
    if (this.user.isNewUser) {
      return false;
    }
    if (this.authService.currentUser.roleCode != "ADMIN") {
      return true;
    }
    return false;
  }

  onRoleChange() {
    if (this.user.role.org == "PLANT") {
      this.user.clinic = null;
    }
    if (this.user.role.org == "LAB") {
      this.user.clinic = null;
      this.user.plant = null;
    }
    if (this.user.role.org == "CLINIC") {
      this.user.plant = null;
    }
    if (this.user.role.id == "DOCTOR") {
      // Doctors are not tied to a specific clinic in user edit.
      this.user.clinic = null;
    }
    if (this.user.role.id != "DOCTOR") {
      this.user.doctor = null;
    }

    this.onClinicChanged();
  }

  onClinicChanged() {
    const allDoctors = this.store.selectSignal(doctorList)();

    // For DOCTOR role we always show the full doctor list (not clinic-filtered).
    if (this.user.role?.id === "DOCTOR") {
      this.filteredDoctorList = allDoctors;
    } else if (this.user.clinic) {
      this.filteredDoctorList = allDoctors.filter((d) =>
        d.clinicList.includes(this.user.clinic.id.toString())
      );
    } else {
      this.filteredDoctorList = allDoctors;
    }

    if (this.user.doctor) {
      this.user.doctor = this.filteredDoctorList.find(
        (d) => d.id == this.user.doctor.id
      );
    }
  }

  public clearNonNumeric(event: any) {
    let rawValue = event.target.value;
    let valueOnlyNumeric = rawValue.replace(/[^0-9]/g, "");
    event.target.value = valueOnlyNumeric;
  }

  public trimSpaces(event: any) {
    let rawValue = event.target.value;
    let rawValueNoSpaces = rawValue.replace(/\s+/g, "");
    event.target.value = rawValueNoSpaces.trim();
  }

  public autoCompleteUsername(event: any) {
    if (this.checkUsernameSubscription) {
      this.checkUsernameSubscription.unsubscribe();
    }

    if (
      !this.user.username ||
      !this.validationPipe.transform(this.user.username, "email")
    ) {
      this.existingUserSw = false;
      return;
    }

    let params = new UsersSearchParams();
    params.username = this.user.username.toUpperCase();
    params.fromUserEditSw = true;
    this.checkUsernameSubscription = this.usersService
      .getUsers(params, false, false)
      .subscribe((data) => {
        if (data && data.length > 0) {
          let existing = data[0];
          this.existingUserSw = true;
          this.user.userDescr = existing.userDescr;
          this.user.password = existing.password;
          this.user.phone = existing.phone;
        } else {
          this.existingUserSw = false;
        }
      });
  }

  public updateUser() {
    this.saving = true;
    this.usersService.updateUser(this.user).subscribe(
      () => {
        this.saving = false;

        this.usersService.syncUsers(this.user).subscribe(
          (data) => {
            this.store.dispatch(
              UPDATE_USER({
                user: this.user,
              })
            );

            this.closePanel();
          },
          (err) => {
            this.store.dispatch(
              UPDATE_USER({
                user: this.user,
              })
            );

            this.closePanel();
          }
        );
      },
      () => (this.saving = false)
    );
  }

  public closePanel() {
    this.onCancel.emit();
  }

  public allowSubmit(): boolean {
    let required: any[] = [this.user.userDescr, this.user.role];

    if (this.user.role) {
      if (this.user.role.org == "PLANT") {
        required.push(this.user.plant);
      } else if (this.user.role.org == "CLINIC") {
        if (this.clinicRequired) {
          required.push(this.user.clinic);
        }
        if (this.user.role.id == "DOCTOR") {
          required.push(this.user.doctor);
        }
      }
    }

    const cond =
      this.validationPipe.transform(this.user.phone, "tel") &&
      this.validatePassword() &&
      required.srp_allTrue();

    if (this.user.isNewUser) {
      return this.validateUsername() && cond;
    }

    return cond;
  }

  validateUsername(): boolean {
    if (!this.user.isNewUser || this.existingUserSw) return true;
    else if (!this.user.username) return false;
    else {
      //let valid = this.validationPipe.transform(this.user.username, "email");
      if (!this.validationPipe.transform(this.user.username, "email")) {
        this.usernameValidationStr = this.invalidUsernameStr;
        return false;
      } else return true;
    }
  }

  validatePassword(): boolean {
    if (!this.user.isNewUser) return true;

    return this.user.password && this.user.password.toString().length >= 8;
  }

  disableUserClicked() {
    let validationStr = this.translateService.instant(
      "translate.users.deleteUserValidation"
    );

    this.sharedService.emitPopupchReceivedEvent({
      title: "translate.users.deleteUserPrompt",
      icon: "exclamation-triangle",
      subTitle: validationStr + " " + this.user.username + "?",
      keep: false,
      approve: () => {
        this.user.activeSw = false;
        this.updateUser();
      },
    });
  }
}
